import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { LhPillClick } from "@liebherr/patternlib";
import {
  LhMultipleOptionSelectEvent,
  PatternlibMultiselectCustomEvent,
  PatternlibPillCustomEvent,
} from "@liebherr/patternlib/dist/types/components";

import { DocumentInfo } from "../../models/document-info.model";
import { Filter } from "../../models/filter.model";
import { FilterGroup } from "../../models/filter-group.model";

@Component({
  selector: "lh-docs-filter",
  templateUrl: "./docs-filter.component.html",
  styleUrls: ["./docs-filter.component.scss"],
})
export class DocsFilterComponent implements OnInit {
  @Input()
  list!: DocumentInfo[];

  @Output()
  filterChanged = new EventEmitter<Filter[]>();

  groups: FilterGroup[] = [
    {
      fieldName: "category",
      options: () => this.getFilterOptions((x) => x.category),
      selectedValues: [],
    },
    {
      fieldName: "documentType",
      options: () => this.getSubFilterOptions((x) => x.documentType),
      selectedValues: [],
    },
    {
      fieldName: "language",
      options: () => this.getLanguageOptions(),
      selectedValues: [],
    },
    {
      fieldName: "includedLmid",
      options: () => this.getSubFilterOptions((x) => x.includedLmid),
      selectedValues: [],
    },
  ];

  showFilter = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.groups.forEach((group) => {
        if (params[group.fieldName]) {
          group.selectedValues = params[group.fieldName].split(",");
        }
      });
      this.notifyFilterChanged();
    });
  }

  notifyFilterChanged() {
    const appliedFilters = this.groups.filter(
      (x) => x.selectedValues.length > 0,
    );

    const params: Params = Object.fromEntries(
      this.groups.map((group) => [
        group.fieldName,
        group.selectedValues.length > 0 ? group.selectedValues.join(",") : null,
      ]),
    );

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
    });

    this.filterChanged.emit(appliedFilters);
  }

  clearFilters() {
    let changed = false;
    for (const group of this.groups) {
      if (group.selectedValues.length > 0) {
        changed = true;
      }
      group.selectedValues = [];
    }
    if (changed) {
      this.notifyFilterChanged();
    }
  }

  private getLanguageOptions(): Array<string> {
    const options = new Array<string>();
    // flatten nested array
    for (const item of this.list.filter(
      (x) =>
        this.groups[0].selectedValues.length == 0 ||
        this.groups[0].selectedValues.includes(x.category),
    )) {
      for (const lang of item.languagesHumanReadable) {
        options.push(lang);
      }
    }
    return Array.from(this.distinct(options)).sort();
  }

  private getFilterOptions<U>(
    propertySelector: (value: DocumentInfo) => U,
  ): Array<U> {
    const options = this.list.map(propertySelector);
    return Array.from(this.distinct(options)).sort();
  }

  private getSubFilterOptions<U>(
    propertySelector: (value: DocumentInfo) => U,
  ): Array<U> {
    const options = this.list
      .filter(
        (x) =>
          this.groups[0].selectedValues.length == 0 ||
          this.groups[0].selectedValues.includes(x.category),
      )
      .map(propertySelector);
    return Array.from(this.distinct(options)).sort();
  }

  private distinct<U>(iterable: Iterable<U>): Iterable<U> {
    return new Set(iterable);
  }

  onPillClick(event: PatternlibPillCustomEvent<LhPillClick>) {
    const label = event.target.label;
    if (this.groups[0].selectedValues.includes(label)) {
      this.groups[0].selectedValues = this.groups[0].selectedValues.filter(
        (y) => y !== label,
      );
    } else {
      this.groups[0].selectedValues.push(label);
    }
    this.notifyFilterChanged();
    const g1Options = this.groups[1].options();

    this.groups[1].selectedValues = this.groups[1].selectedValues.filter((x) =>
      g1Options.includes(x),
    );
    this.notifyFilterChanged();
  }

  onMultiselect(
    filedName: string,
    event: PatternlibMultiselectCustomEvent<LhMultipleOptionSelectEvent>,
  ) {
    const group = this.groups.filter((x) => x.fieldName === filedName)[0];
    group.selectedValues =
      event.detail.selected?.map((x) => x.value.toString()) ?? [];
    this.notifyFilterChanged();
  }

  onShowFilter() {
    this.showFilter = !this.showFilter;
  }

  onResetClicked() {
    this.clearFilters();
  }
}
